import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { gotoApp } from "@/lib/appUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/components/GoogleButton";

const field =
  "h-11 bg-zinc-900/60 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/40";

export default function Login() {
  const { login, subscription } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    api.get("/auth/providers").then((r) => setGoogleEnabled(!!r.data.google)).catch(() => setGoogleEnabled(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNeedsVerify(false);
    try {
      const data = await login(email, password);
      const sub = await subscription();
      // a plan chosen before signing in wins, so the user resumes checkout
      const chosePlan = !!sessionStorage.getItem("pj_plan");
      const target = chosePlan || (sub && (sub.status === "expired" || sub.proposals_left <= 0)) ? "/plans" : "/dashboard";
      if (await gotoApp(data.access_token, target)) return;
      navigate(target);
    } catch (err) {
      const message = apiError(err);
      if (err?.response?.status === 403 && /verify|confirm/i.test(message)) setNeedsVerify(true);
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResent("sending");
    try {
      await api.post("/auth/resend-verification-public", { email });
      setResent("sent");
    } catch (e) {
      setResent(apiError(e));
    }
  };

  return (
    <div className="min-h-screen nv-grid-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[420px] nv-rise">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-2.5">
            <Logo size={30} withWordmark className="text-lg" testid="auth-logo" />
          </div>
          <ThemeToggle />
        </div>

        <p className="text-[11px] font-num uppercase tracking-[0.24em] text-indigo-400 mb-3">Proposal OS</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Sign in to your workspace</h1>
        <p className="text-sm text-zinc-500 mt-2 mb-8">Branded, AI-drafted proposals that close themselves.</p>

        <form onSubmit={submit} className="space-y-3">
          {googleEnabled && (
            <>
              <GoogleButton label="Continue with Google" testid="google-login-btn" onError={setError} />
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-zinc-800" />
                <span className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-600">or email</span>
                <span className="h-px flex-1 bg-zinc-800" />
              </div>
            </>
          )}

          <Input data-testid="login-email-input" className={field} type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input data-testid="login-password-input" className={field} type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className="flex justify-end">
            <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-zinc-500 hover:text-indigo-400 transition-colors">
              Forgot password?
            </Link>
          </div>

          {error && !needsVerify && (
            <p data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {needsVerify && (
            <div data-testid="login-verify-required" className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-3 space-y-2">
              <p className="text-sm text-amber-300 flex items-start gap-2">
                <MailCheck className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Confirm your email before signing in — we sent a link to{" "}
                  <span className="font-medium break-all">{email}</span>
                </span>
              </p>
              <button
                type="button"
                data-testid="login-resend-verification"
                onClick={resend}
                disabled={resent === "sending" || resent === "sent"}
                className="text-xs font-medium text-amber-200 underline underline-offset-2 disabled:opacity-60"
              >
                {resent === "sent" ? "Verification email sent" : resent === "sending" ? "Sending…" : "Resend verification email"}
              </button>
              {resent && resent !== "sending" && resent !== "sent" && <p className="text-xs text-red-300">{resent}</p>}
            </div>
          )}

          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}
          </Button>
        </form>

        <p className="text-sm text-zinc-500 mt-6">
          No workspace yet?{" "}
          <Link data-testid="goto-signup-link" to="/signup" className="text-indigo-400 hover:text-indigo-300">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
